import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useEditCompanyGroups } from '../../../context/EditCompanyGroupsContext'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'

interface CompaniesInGroupTableProps {
  companyId: string
}

const CompaniesInGroupTable: React.FC<CompaniesInGroupTableProps> = ({ companyId }) => {
  const { t } = useLocalize()
  const { data: companies = [] } = useQueryCompanies({ includeDeleted: false })
  const {
    companiesInGroup,
    selectedGroup,
    toggleId,
    toggleAll,
    loading,
  } = useEditCompanyGroups()

  const allIds = companies.map((c) => c.id)
  const allSelected = allIds.every((id) => selectedGroup.includes(id))
  const someSelected = allIds.some((id) => selectedGroup.includes(id)) && !allSelected

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <div className="tw-text-sm tw-text-gray-500">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="tw-w-full">
      <table className="tw-w-full tw-border-collapse">
        <thead className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
          <tr>
            <th className="tw-p-3 tw-text-left">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = someSelected
                  }
                }}
                onChange={(e) => toggleAll(e.target.checked)}
                className="tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-border-gray-300 tw-text-primary focus:tw-ring-primary"
              />
            </th>
            <th className="tw-p-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-700">
              {t('addCompany.companyName')}
            </th>
            <th className="tw-p-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-700">
              {t('addCompany.identifier')}
            </th>
            <th className="tw-p-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-700">
              {t('common.status')}
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map((company) => {
            const companyId = company.id
            const isChecked = selectedGroup.includes(companyId)
            const wasInitiallyInGroup = companiesInGroup.includes(companyId)

            return (
              <tr
                key={company.id}
                className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 ${
                  isChecked !== wasInitiallyInGroup ? 'tw-bg-yellow-50' : ''
                }`}
              >
                <td className="tw-p-3">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleId(companyId)}
                    className="tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-border-gray-300 tw-text-primary focus:tw-ring-primary"
                  />
                </td>
                <td className="tw-p-3 tw-text-sm tw-text-gray-900">
                  {company.companyName}
                </td>
                <td className="tw-p-3 tw-text-sm tw-text-gray-600">
                  {company.uniqueIdentifier}
                </td>
                <td className="tw-p-3 tw-text-sm">
                  {company.isActive ? (
                    <span className="tw-rounded-full tw-bg-green-100 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-green-800">
                      {t('common.active')}
                    </span>
                  ) : (
                    <span className="tw-rounded-full tw-bg-gray-100 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-800">
                      {t('common.inactive')}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {companies.length === 0 && (
        <div className="tw-p-8 tw-text-center tw-text-sm tw-text-gray-500">
          {t('addCompany.noCompaniesToDisplay')}
        </div>
      )}
    </div>
  )
}

export default CompaniesInGroupTable
